import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Key as KeyIcon } from '@mui/icons-material';
import { registerPasskey, clearPasskeyError } from '../../PasskeyActions';

const messages = defineMessages({
  title: {
    id: 'PasskeySetup.Title',
    defaultMessage: 'Set Up Passkey',
  },
  description: {
    id: 'PasskeySetup.Description',
    defaultMessage: 'Choose a name for your passkey and follow the prompts to register it.',
  },
  passkeyName: {
    id: 'Passkey.Name',
    defaultMessage: 'Passkey Name',
  },
  passkeyNamePlaceholder: {
    id: 'Passkey.NamePlaceholder',
    defaultMessage: 'e.g., iPhone, MacBook, YubiKey',
  },
  register: {
    id: 'Passkey.Register',
    defaultMessage: 'Register',
  },
  cancel: {
    id: 'Common.Cancel',
    defaultMessage: 'Cancel',
  },
  setupComplete: {
    id: 'PasskeySetup.Complete',
    defaultMessage: 'Passkey setup completed successfully!',
  },
});

// Helper function to get device name
const getDeviceName = () => {
  const { userAgent } = navigator;

  // Mobile devices
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Android/i.test(userAgent)) {
    // Try to extract device name from Android user agent
    const match = userAgent.match(/Android.*?;.*?([^;]+)\)/);
    return match && match[1] ? match[1].trim() : 'Android Device';
  }

  // Desktop platforms - using userAgent instead of deprecated platform
  if (/Mac OS X/i.test(userAgent) || /Macintosh/i.test(userAgent)) return 'Mac';
  if (/Windows/i.test(userAgent)) return 'Windows PC';
  if (/Linux/i.test(userAgent)) return 'Linux Device';

  // Browser-specific fallbacks
  if (/Chrome/i.test(userAgent)) return 'Chrome Browser';
  if (/Firefox/i.test(userAgent)) return 'Firefox Browser';
  if (/Safari/i.test(userAgent)) return 'Safari Browser';
  if (/Edge/i.test(userAgent)) return 'Edge Browser';

  // Final fallback
  return 'This Device';
};

function PasskeySetup({ onComplete, onError, open, onClose }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [passkeyName, setPasskeyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    if (open) {
      setPasskeyName(getDeviceName());
      setError('');
      setSetupComplete(false);
      dispatch(clearPasskeyError());
    }
  }, [open, dispatch]);

  const handleRegisterPasskey = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      await dispatch(registerPasskey(passkeyName || getDeviceName()));
      setSetupComplete(true);

      // Wait a moment to show success, then complete
      setTimeout(() => {
        if (onComplete) {
          onComplete({ success: true, method: 'passkey' });
        }
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to register passkey');
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, passkeyName, onComplete, onError]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <KeyIcon sx={{ mr: 1, color: 'primary.main' }} />
          {formatMessage(messages.title)}
        </Box>
      </DialogTitle>

      <DialogContent>
        {setupComplete ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <KeyIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              {formatMessage(messages.setupComplete)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                id="PasskeySetup.Redirecting"
                defaultMessage="Redirecting to your account..."
              />
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {formatMessage(messages.description)}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label={formatMessage(messages.passkeyName)}
              placeholder={formatMessage(messages.passkeyNamePlaceholder)}
              value={passkeyName}
              onChange={e => setPasskeyName(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </>
        )}
      </DialogContent>

      {!setupComplete && (
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            onClick={handleRegisterPasskey}
            variant="contained"
            disabled={isLoading || !passkeyName.trim()}
            startIcon={isLoading ? <CircularProgress size={16} /> : <KeyIcon />}
          >
            {isLoading ? (
              <FormattedMessage id="Common.Registering" defaultMessage="Registering..." />
            ) : (
              formatMessage(messages.register)
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default PasskeySetup;
